import { type ReactNode, useCallback, useState } from "react";
import { Spinner } from "./Spinner.tsx";

export interface TableColumn<T> {
	id: string;
	header: string;
	accessor: (row: T) => ReactNode;
	sortable?: boolean;
	className?: string;
}

export type SortDirection = "asc" | "desc";

export interface TableSort {
	columnId: string;
	direction: SortDirection;
}

export interface TableProps<T> {
	columns: TableColumn<T>[];
	data: T[];
	keyExtractor: (row: T) => string;
	loading?: boolean;
	emptyMessage?: string;
	sort?: TableSort;
	onSortChange?: (sort: TableSort) => void;
	className?: string;
}

function SortIcon({ direction }: { direction?: SortDirection }) {
	if (!direction) {
		return (
			<svg
				aria-hidden="true"
				className="ml-1 inline h-4 w-4 text-muted"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
				/>
			</svg>
		);
	}
	return (
		<svg
			aria-hidden="true"
			className="ml-1 inline h-4 w-4 text-content"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth="1.5"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d={direction === "asc" ? "M4.5 15.75l7.5-7.5 7.5 7.5" : "M19.5 8.25l-7.5 7.5-7.5-7.5"}
			/>
		</svg>
	);
}

/** The sortable header's control. Non-sortable columns stay plain markup so a
 *  screen reader is not told about a button that does nothing. */
function Cell({
	sortable,
	onSort,
	label,
	children,
}: {
	sortable?: boolean;
	onSort: () => void;
	label: string;
	children: ReactNode;
}) {
	if (!sortable) return <span className="inline-flex items-center">{children}</span>;
	return (
		<button
			type="button"
			onClick={onSort}
			title={`Sort by ${label}`}
			className="inline-flex select-none items-center uppercase tracking-wider hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
		>
			{children}
		</button>
	);
}

export function Table<T>({
	columns,
	data,
	keyExtractor,
	loading = false,
	emptyMessage = "No data available",
	sort,
	onSortChange,
	className = "",
}: TableProps<T>) {
	const [internalSort, setInternalSort] = useState<TableSort | undefined>(sort);
	const currentSort = sort ?? internalSort;

	const handleSort = useCallback(
		(columnId: string) => {
			const newDirection: SortDirection =
				currentSort?.columnId === columnId && currentSort.direction === "asc" ? "desc" : "asc";
			const newSort: TableSort = { columnId, direction: newDirection };
			if (onSortChange) {
				onSortChange(newSort);
			} else {
				setInternalSort(newSort);
			}
		},
		[currentSort, onSortChange],
	);

	return (
		<div className={`overflow-x-auto ${className}`}>
			<table className="min-w-full divide-y divide-line">
				<thead className="bg-surface">
					<tr>
						{columns.map((column) => (
							<th
								key={column.id}
								scope="col"
								className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted ${column.className ?? ""}`}
								aria-sort={
									currentSort?.columnId === column.id
										? currentSort.direction === "asc"
											? "ascending"
											: "descending"
										: undefined
								}
							>
								{/* The sort control is a button rather than a click handler on the
								    header cell: the original was reachable by mouse only. */}
								<Cell
									sortable={column.sortable}
									onSort={() => handleSort(column.id)}
									label={typeof column.header === "string" ? column.header : column.id}
								>
									{column.header}
									{column.sortable && (
										<SortIcon
											direction={
												currentSort?.columnId === column.id ? currentSort.direction : undefined
											}
										/>
									)}
								</Cell>
							</th>
						))}
					</tr>
				</thead>
				<tbody className="divide-y divide-line bg-surface">
					{loading ? (
						<tr>
							<td colSpan={columns.length} className="px-6 py-12 text-center">
								<Spinner size="lg" className="text-muted" />
							</td>
						</tr>
					) : data.length === 0 ? (
						<tr>
							<td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-muted">
								{emptyMessage}
							</td>
						</tr>
					) : (
						data.map((row) => (
							<tr key={keyExtractor(row)} className="transition-colors hover:bg-surface-2">
								{columns.map((column) => (
									<td
										key={column.id}
										className={`whitespace-nowrap px-6 py-4 text-sm text-content ${column.className ?? ""}`}
									>
										{column.accessor(row)}
									</td>
								))}
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
}
